import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, MapPin, Trash2, Save, ChevronRight, ExternalLink, X } from 'lucide-react';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';

interface Site {
  id: string;
  name: string;
  site_reference?: string;
  client_id: string;
  client_name?: string | null;
  broker_id?: string;
  broker_name?: string | null;
  insurer_id?: string;
  physical_address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  place_id?: string | null;
  geofence_threshold_m?: number | null;
  site_contact_name?: string;
  site_contact_phone?: string;
  default_survey_template_id?: string | null;
  active?: boolean;
}

interface ClientOption { id: string; name: string; broker_id?: string; }
interface TemplateOption { id?: string; _id?: string; name: string; }

const emptyForm = {
  client_id: '', name: '', site_reference: '',
  default_survey_template_id: '',
  physical_address: '', city: '', province: '', postal_code: '',
  site_contact_name: '', site_contact_phone: '',
  // Google Places metadata + optional per-site geofence override
  place_id: '' as string | '',
  latitude: null as number | null,
  longitude: null as number | null,
  geofence_threshold_m: null as number | null,
};

export const SitesPage: React.FC = () => {
  const nav = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [q, setQ] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Client-side pagination — rendering 2 500+ rows at once froze the page.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const pagedSites = useMemo(() => sites.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE), [sites, page]);
  const pageCount = Math.max(1, Math.ceil(sites.length / PAGE_SIZE));
  useEffect(() => { setPage(0); }, [sites]);

  const loadClients = async () => {
    try {
      const r = await api.get<ClientOption[]>('/clients');
      setClients(r.data);
      // Deliberately do NOT auto-preselect the first client. When the
      // dropdown value stays empty, we render a "— Choose a client —"
      // placeholder option so the visible state matches React state.
      // Auto-preselecting caused the "please choose a client" bug: the
      // browser showed the first option, but state was still '', so
      // submit failed validation.
    } catch (err) { console.warn('clients dropdown load failed', err); }
  };
  const loadTemplates = async () => {
    try {
      const r = await api.get<TemplateOption[]>('/templates');
      setTemplates(r.data);
    } catch (err) { console.warn('templates dropdown load failed', err); }
  };
  const loadSites = async () => {
    try {
      const params: Record<string, string> = {};
      if (q) params.q = q;
      if (clientFilter) params.client_id = clientFilter;
      const r = await api.get<Site[]>('/sites', { params });
      setSites(r.data);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load sites');
    }
  };

  useEffect(() => { void loadClients(); void loadTemplates(); void loadSites(); /* eslint-disable-line */ }, []);
  useEffect(() => { const t = setTimeout(() => void loadSites(), 250); return () => clearTimeout(t); /* eslint-disable-line */ }, [q, clientFilter]);

  const submit = async () => {
    if (!form.client_id) { toast.error('Choose a client'); return; }
    if (!form.name.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/sites/${editingId}`, form);
        toast.success('Site updated');
      } else {
        await api.post('/sites', form);
        toast.success('Site created');
      }
      setForm(emptyForm); setCreating(false); setEditingId(null);
      void loadSites();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (s: Site) => {
    setEditingId(s.id);
    setForm({
      client_id: s.client_id, name: s.name || '',
      site_reference: s.site_reference || '',
      default_survey_template_id: s.default_survey_template_id || '',
      physical_address: s.physical_address || '',
      city: s.city || '', province: s.province || '',
      postal_code: s.postal_code || '',
      site_contact_name: s.site_contact_name || '',
      site_contact_phone: s.site_contact_phone || '',
      place_id: s.place_id || '',
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
      geofence_threshold_m: s.geofence_threshold_m ?? null,
    });
    setCreating(true);
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this site? This cannot be undone.')) return;
    try {
      await api.delete(`/sites/${id}`);
      toast.success('Site deleted');
      void loadSites();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    }
  };

  const clientById = useMemo(() => {
    const m: Record<string, ClientOption> = {};
    clients.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clients]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Sites</h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            Physical locations that get inspected. Each site belongs to a client and inherits their broker for RBAC scope.
          </p>
        </div>
        <Button variant="gradient" onClick={() => { setEditingId(null); setForm(emptyForm); setCreating(!creating); }} data-testid="new-site-btn" disabled={clients.length === 0}>
          <Plus size={16} className="mr-1" /> {creating ? 'Cancel' : 'New site'}
        </Button>
      </div>

      {clients.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-raptor-navy/70 font-body">
            You need at least one client before you can create sites. <a href="/insurer/clients" className="text-raptor-blue underline">Create a client</a> first.
          </CardContent>
        </Card>
      )}

      {creating && clients.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-headline text-xl text-raptor-navy">{editingId ? 'Edit site' : 'New site'}</CardTitle>
            <CardDescription>Sites inherit their insurer &amp; broker from the parent client automatically.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {/* ─── 1. Identity ────────────────────────────────── */}
              <div>
                <Label>Client *</Label>
                <Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="site-client">
                  <option value="">— Choose a client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
                {!form.client_id && (
                  <p className="text-[11px] text-raptor-navy/50 font-body pt-0.5">
                    Every site must belong to a client. Broker and insurer are inherited automatically.
                  </p>
                )}
              </div>
              <div><Label>Site name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Mall Sandton" data-testid="site-name" /></div>
              <div className="md:col-span-2"><Label>Site reference</Label><Input value={form.site_reference} onChange={(e) => setForm({ ...form, site_reference: e.target.value })} placeholder="Deposita ref, red number, or your own code" /></div>
              <div className="md:col-span-2">
                <Label>Default survey template</Label>
                <Select value={form.default_survey_template_id} onChange={(e) => setForm({ ...form, default_survey_template_id: e.target.value })} data-testid="site-default-template">
                  <option value="">— None (choose at issue) —</option>
                  {templates.map((t) => <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>)}
                </Select>
                <p className="text-[11px] text-raptor-navy/50 mt-1 font-body">Pre-selected when issuing or bulk-dispatching surveys for this site. You can still change it before sending.</p>
              </div>

              {/* ─── 2. Location (address first, drives autofill) ── */}
              <div className="md:col-span-2 pt-1 border-t border-border/40 mt-2">
                <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Location</div>
                <Label>Physical address *</Label>
                <AddressAutocomplete
                  value={form.physical_address}
                  onChange={(v: string) => setForm({ ...form, physical_address: v })}
                  onResolve={(p) => {
                    // Autofill city/province/postal + coords + place_id from
                    // whatever the operator just picked. Manual edits after
                    // selection remain sticky (this fires only on pick).
                    setForm((f) => ({
                      ...f,
                      physical_address: p.formatted_address,
                      city: p.city || f.city,
                      province: p.province || f.province,
                      postal_code: p.postal_code || f.postal_code,
                      latitude: p.lat,
                      longitude: p.lng,
                      place_id: p.place_id || '',
                    }));
                  }}
                  testid="site-address"
                  placeholder="Start typing to auto-fill city, province, postal code and GPS…"
                />
                {form.latitude != null && form.longitude != null && (
                  <div className="mt-1 text-[11px] text-emerald-700 font-tabular">
                    ✓ Coords captured: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                  </div>
                )}
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>Province</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
              <div><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>

              {/* ─── 3. Contact ─────────────────────────────────── */}
              <div className="md:col-span-2 pt-1 border-t border-border/40 mt-2">
                <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Site contact</div>
              </div>
              <div><Label>Contact name</Label><Input value={form.site_contact_name} onChange={(e) => setForm({ ...form, site_contact_name: e.target.value })} /></div>
              <div><Label>Contact phone</Label><Input value={form.site_contact_phone} onChange={(e) => setForm({ ...form, site_contact_phone: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); setForm(emptyForm); }}>Cancel</Button>
              <Button variant="gradient" onClick={() => void submit()} disabled={saving} data-testid="site-save-btn"><Save size={14} className="mr-1" /> {saving ? 'Saving…' : editingId ? 'Update site' : 'Create site'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="font-headline">All sites ({sites.length})</CardTitle>
          <div className="flex gap-2">
            <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="h-8 w-48" data-testid="site-client-filter">
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-7 h-8 w-56" data-testid="site-search" />
            </div>
            {(clientFilter || q) && (
              <Button variant="ghost" size="sm" onClick={() => { setClientFilter(''); setQ(''); }} data-testid="site-reset-filters">
                <X size={14} /> Reset filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {sites.length === 0 ? (
            <div className="p-10 text-center text-raptor-navy/50 font-body">
              <MapPin size={40} className="mx-auto mb-2 opacity-40" />
              No sites yet. Create a client, then add sites under it.
            </div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-left text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3">Site</th>
                  <th className="py-2 px-3">Reference</th>
                  <th className="py-2 px-3">Client</th>
                  <th className="py-2 px-3">Broker</th>
                  <th className="py-2 px-3">City / Province</th>
                  <th className="py-2 px-3">Contact</th>
                </tr>
              </thead>
              <tbody>
                {pagedSites.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                    data-testid={`site-row-${s.id}`}
                    onClick={() => nav(`/insurer/sites/${s.id}`)}
                  >
                    <td className="py-2 px-3 font-medium text-raptor-navy inline-flex items-center gap-1">
                      {s.name}
                      <ChevronRight size={12} className="text-raptor-navy/30" />
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70 font-tabular text-xs">{s.site_reference ?? '—'}</td>
                    <td className="py-2 px-3 text-raptor-navy/70">
                      {s.client_id ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 hover:text-raptor-blue hover:underline text-left"
                          onClick={(e) => { e.stopPropagation(); nav(`/insurer/clients/${s.client_id}?tab=sites`); }}
                        >
                          {s.client_name || clientById[s.client_id]?.name || s.client_id}
                          <ExternalLink size={10} />
                        </button>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70 text-xs">
                      {s.broker_id ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 hover:text-raptor-blue hover:underline"
                          onClick={(e) => { e.stopPropagation(); nav(`/insurer/brokers/${s.broker_id}`); }}
                        >
                          {s.broker_name || s.broker_id}
                          <ExternalLink size={10} />
                        </button>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70">
                      {s.city ?? '—'}{s.province ? ` · ${s.province}` : ''}
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70">
                      {s.site_contact_name || '—'}
                      {s.site_contact_phone && <div className="text-xs text-raptor-navy/50">{s.site_contact_phone}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {sites.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border/60 text-xs font-body" data-testid="sites-pager">
              <span className="text-raptor-navy/60">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sites.length)} of {sites.length}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="sites-prev-page">Prev</Button>
                <span className="px-2 text-raptor-navy/70 font-tabular">Page {page + 1} / {pageCount}</span>
                <Button size="sm" variant="outline" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} data-testid="sites-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SitesPage;
